import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, BookOpen, AlertTriangle } from "lucide-react";

const API = "/api";

/**
 * Landing page after returning from PayMongo's hosted Checkout Session.
 * We poll /status/{cid} which re-fetches the session from PayMongo and credits
 * the student the moment the latest payment intent is "paid".
 */
export default function PayMongoResult() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const cid = params.get("cid");
  const initialStatus = params.get("status"); // "success" | "cancelled"

  const [data, setData] = useState(null);
  const [status, setStatus] = useState(
    initialStatus === "cancelled" ? "cancelled" : "checking"
  );
  // checking | success | failed | cancelled | not_found

  useEffect(() => {
    if (!cid || initialStatus === "cancelled") return;
    let attempts = 0;
    const maxAttempts = 12;
    let cancelled = false;

    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/paymongo/status/${cid}`);
        if (cancelled) return;
        setData(r.data);
        if (r.data.payment_status === "paid") {
          setStatus("success");
          return;
        }
        if (r.data.payment_status === "failed") {
          setStatus("failed");
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setStatus("failed");
        }
      } catch {
        if (!cancelled) setStatus("not_found");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [cid, initialStatus]);

  return (
    <div translate="no" className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg border-slate-200" data-testid="paymongo-result-page">
        <CardContent className="pt-8 pb-8 text-center space-y-4">
          {status === "checking" && (
            <>
              <Loader2 className="w-12 h-12 text-emerald-600 animate-spin mx-auto" />
              <h2 className="text-xl font-bold text-slate-900">
                Confirming your payment…
              </h2>
              <p className="text-slate-500 text-sm">
                Verifying your transaction with PayMongo. This usually takes 2–10 seconds.
              </p>
            </>
          )}

          {status === "success" && data && (
            <>
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-9 h-9 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900" data-testid="paymongo-success-title">
                Payment Successful!
              </h2>
              <div className="bg-slate-50 rounded-lg p-4 text-left text-sm space-y-1">
                <p><strong>Student:</strong> {data.student_name}</p>
                <p>
                  <strong>Classes added:</strong>{" "}
                  <span className="text-green-700 font-bold">+{data.classes_purchased}</span>
                </p>
                <p>
                  <strong>Amount paid:</strong> {data.amount} {data.currency_symbol}
                </p>
              </div>
              <Button
                onClick={() => navigate("/student-dashboard")}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="paymongo-go-dashboard"
              >
                <BookOpen className="w-4 h-4 mr-2" /> Go to Dashboard
              </Button>
            </>
          )}

          {status === "cancelled" && (
            <>
              <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                <XCircle className="w-9 h-9 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Payment Cancelled</h2>
              <p className="text-slate-600 text-sm">
                You cancelled the payment. No money has been charged.
              </p>
              <Button
                onClick={() => navigate("/subscribe")}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                Try Again
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/student-dashboard")}
                className="w-full"
              >
                Back to Dashboard
              </Button>
            </>
          )}

          {status === "failed" && (
            <>
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="w-9 h-9 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Payment Failed</h2>
              <p className="text-slate-600 text-sm">
                Your bank declined the transaction or it timed out. Please try again or use a different card / e-wallet.
              </p>
              <Button
                onClick={() => navigate("/subscribe")}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                Try Again
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/student-dashboard")}
                className="w-full"
              >
                Back to Dashboard
              </Button>
            </>
          )}

          {status === "not_found" && (
            <>
              <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-9 h-9 text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">
                Couldn't verify your payment
              </h2>
              <p className="text-slate-600 text-sm">
                We couldn't find this transaction. If you were charged, the credit
                usually arrives within a few minutes. Please refresh your dashboard
                or contact support with this reference: <code className="bg-slate-100 px-1 rounded">{cid}</code>
              </p>
              <Button
                onClick={() => navigate("/student-dashboard")}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                Back to Dashboard
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
